import React, { useState, useEffect, useMemo } from "react";
import { Flex, Text, SegmentedControl, Card, Grid, Dialog, Button, Box, TextField, TextArea, Select, AlertDialog, DropdownMenu,
  Badge, Switch, Checkbox, Separator, Progress, Tooltip } from "@radix-ui/themes";
import { EnvelopeClosedIcon, TimerIcon, ChatBubbleIcon, MobileIcon, PlusIcon, PersonIcon } from "@radix-ui/react-icons";
import { useMarketingAutomations, useUpdateAutomation, useDeleteAutomation, useCreateAutomation, useGetAllBrands, useGetWhatsappCredentialAccounts, useMarketingEmailTemplates, useMarketingWhatsappTemplates } from "hooks";
import { useContactTags } from "hooks/bMarketing/useContacts";
import { useProfileContext, useLocationContext } from "context";
import { allTimezones, useTimezoneSelect } from "react-timezone-select";

import ContactFilterBuilder, { needsValue } from "./ContactFilterBuilder";
import ElegantClassicTemplate from './templates/ElegantClassicEmailTemplate';
import ThankYouReviewTemplate from './templates/ThankYouReviewTemplate';
import HospitalityPromoTemplate from './templates/HospitalityPromoTemplate';
import MarketingWhatsappAutomations, {
  AutomationFields as WhatsAppAutomationFields,
  buildAutomationPayload as buildWhatsappAutomationPayload,
  buildDraftAutomation as buildWhatsappDraftAutomation,
} from "./MarketingWhatsappAutomations";

// Section order for automations
const SECTION_ORDER = ['welcome', 'newsletter', 'review', 'promotion', 'whatsapp_review', 'whatsapp_birthday'];

const TEMPLATES = {
  'elegant-classic': ElegantClassicTemplate,
  'thank-you-review': ThankYouReviewTemplate,
  'hospitality-promo': HospitalityPromoTemplate,
};

const PURPOSE_TEMPLATE_MAP = {
  welcome: 'elegant-classic',
  review: 'thank-you-review',
  promotion: 'hospitality-promo',
  newsletter: 'elegant-classic',
};

const EMPTY_TEMPLATE_OPTION_VALUE = '__no_templates_available__';
const WHATSAPP_AUTOMATION_INTEGRATION_REQUIRED_MESSAGE = "Connect a WhatsApp integration before creating WhatsApp automations.";
const BIRTHDAY_OFFSET_OPTIONS = [
  { value: 0, label: 'On their birthday' },
  { value: 1, label: '1 day before' },
  { value: 3, label: '3 days before' },
  { value: 7, label: '7 days before' },
  { value: 14, label: '14 days before' },
  { value: 21, label: '21 days before' },
  { value: 28, label: '28 days before' },
  { value: 30, label: '30 days before' },
];

const inferAudienceTimezone = (automation, locations) => {
  if (!automation || !Array.isArray(locations)) return '';
  let relevant = locations;
  if (automation.audience_level === 'location') {
    const ids = new Set((automation.audience_locations || []).map(String));
    relevant = locations.filter((location) => ids.has(String(location.id)));
  } else if (automation.audience_level === 'brand') {
    const ids = new Set((automation.audience_brands || []).map(String));
    relevant = locations.filter((location) => ids.has(String(location.brand?.id ?? location.brand_id ?? location.brand)));
  }
  const timezones = [...new Set(relevant.map((location) => location.timezone).filter(Boolean))];
  return timezones.length === 1 ? timezones[0] : '';
};

const getTemplateSelectValue = (template) => {
  const templateId = template?.template_id ?? template?.id;
  if (templateId === undefined || templateId === null) return null;

  const value = String(templateId).trim();
  return value || null;
};

const getSelectedTemplateValue = (templateId) => {
  if (templateId === undefined || templateId === null) return '';

  return String(templateId);
};

const formatAutomationDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const AutomationTimestamp = ({ value }) => {
  const formattedValue = formatAutomationDateTime(value);

  return (
    <Tooltip content={formattedValue}>
      <Text
        as="div"
        size="1"
        weight="medium"
        style={{ width: "100%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {formattedValue}
      </Text>
    </Tooltip>
  );
};

const AutomationTriggerBadge = ({ triggerType }) => {
  const isBirthday = triggerType === "birthday";

  return (
    <Badge color={isBirthday ? "violet" : "blue"} variant="soft" style={{ alignSelf: "flex-start" }}>
      {isBirthday ? "Birthday trigger" : "Post-visit trigger"}
    </Badge>
  );
};

// Normalise the eligibility filter into the API payload shape, dropping incomplete
// rules. Returns {} when no valid rules exist (meaning "all contacts eligible").
const buildEligibilityPayload = (filters) => {
  const rules = (filters?.rules || []).filter((r) => {
    if (!r.field || !r.operator) return false;
    if (needsValue(r.operator)) {
      if (Array.isArray(r.value)) return r.value.length > 0;
      return r.value !== "" && r.value !== undefined && r.value !== null;
    }
    return true;
  });
  return rules.length ? { match: filters?.match || "all", rules } : {};
};

// Helper to format audience level display
const formatAudienceLevel = (level) => {
  return level.charAt(0).toUpperCase() + level.slice(1);
};

// Helper to format sub_type display
const formatSubType = (subType) => {
  switch (subType) {
    case 'welcome':
      return 'Welcome email';
    case 'newsletter':
      return 'Newsletter';
    case 'review':
      return 'Review email';
    case 'promotion':
      return 'Promotional email';
    case 'whatsapp_review':
      return 'WhatsApp review request';
    case 'whatsapp_birthday':
      return 'WhatsApp birthday offer';
    default:
      return subType;
  }
};

const getAutomationSectionKey = (automation) => {
  if (automation?.type === 'whatsapp') {
    return automation.trigger_type === 'birthday' ? 'whatsapp_birthday' : 'whatsapp_review';
  }

  return automation?.sub_type;
};

const getTemplatePreviewHtml = (template) => {
  if (!template) return "";

  const isImported = template.template_source && template.template_source !== 'internal';

  if (template.template_type === 'scratch') {
    if (template.html_content) return template.html_content;

    const orgName = template.name || 'Your Brand';
    return `<div style="background:#F3F4F6;padding:24px 20px;font-family:Arial,sans-serif;">
      <div style="max-width:600px;margin:0 auto;background:#FFFFFF;border-radius:10px;overflow:hidden;box-shadow:0 1px 8px rgba(0,0,0,0.07);">
        <div style="padding:80px 30px;text-align:center;">
          <div style="font-size:16px;font-weight:700;margin-bottom:6px;color:#111827;">Start building your email</div>
          <div style="font-size:14px;color:#9CA3AF;">Drag blocks from the left panel, or click them to add.</div>
        </div>
        <div style="border-top:1px solid #e5e7eb;background:#f9fafb;padding:28px 30px 24px;text-align:center;">
          <div style="font-size:13px;color:#9CA3AF;">&copy; ${new Date().getFullYear()} ${orgName}. All rights reserved.</div>
        </div>
      </div>
    </div>`;
  }

  if (isImported && template.html_content) {
    return template.html_content;
  }

  const designJson = template.design_json || {};
  const templateId = designJson.templateId || PURPOSE_TEMPLATE_MAP[template.purpose] || 'elegant-classic';
  const templateModule = TEMPLATES[templateId];

  if (!templateModule) return "";

  const formData = {
    ...templateModule.getDefaultValues(template.purpose),
    ...designJson,
  };

  return templateModule.generatePreviewHtml(formData);
};

const TemplatePreviewPanel = ({ template }) => {
  const previewHtml = useMemo(() => getTemplatePreviewHtml(template), [template]);

  return (
    <Flex direction="column" gap="2" style={{ flex: '0 0 420px', minWidth: 320, minHeight: 0, overflow: 'hidden' }}>
      <Text weight="medium" size="2" align="center" justify="center" color="gray">Template Preview</Text>
      <Box style={{
        flex: '1 1 0',
        minHeight: 0,
        border: '1px solid var(--gray-a6)',
        borderRadius: 'var(--radius-3)',
        background: 'var(--gray-2)',
        overflow: 'hidden'
      }}>
        {previewHtml ? (
          <iframe
            title={template?.name ? `${template.name} preview` : 'Template preview'}
            srcDoc={previewHtml}
            sandbox=""
            style={{ width: '100%', height: '100%', border: 0, background: '#FFFFFF' }}
          />
        ) : (
          <Flex direction="column" align="center" justify="center" gap="2" height="100%" p="5" style={{ textAlign: 'center' }}>
            <Text weight="medium" size="2">No template selected</Text>
            <Text size="2" color="gray">Choose an email template to preview it here.</Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

const EmailTriggerAndEligibilityFields = ({ automation, onChange }) => {
  const timezones = { ...allTimezones, 'Europe/Berlin': 'Frankfurt' };
  const { options: timezoneOptions } = useTimezoneSelect({ labelStyle: 'original', timezones });
  const selectedOffset = Number(automation.birthday_send_offset_days ?? 0);
  const hasPresetOffset = BIRTHDAY_OFFSET_OPTIONS.some((option) => option.value === selectedOffset);
  const hasTimezoneOption = timezoneOptions?.some((option) => option.value === automation.birthday_timezone);

  return (
  <Flex direction="column" gap="3" style={{ alignSelf: 'stretch' }}>
    <Flex direction="column" gap="1">
      <Text weight="medium" size="2">Trigger</Text>
      <Select.Root
        value={automation.trigger_type || 'guest_visit'}
        onValueChange={(value) => onChange({
          trigger_type: value,
          ...(value === 'birthday' ? {
            birthday_send_time: automation.birthday_send_time || '09:00',
            birthday_timezone: automation.birthday_timezone || 'Europe/London',
          } : {}),
        })}
      >
        <Select.Trigger style={{ width: '100%' }} />
        <Select.Content>
          <Select.Item value="guest_visit">Guest visit</Select.Item>
          <Select.Item value="birthday">Birthday</Select.Item>
        </Select.Content>
      </Select.Root>
    </Flex>

    {(automation.trigger_type || 'guest_visit') === 'birthday' && (
      <>
        <Flex gap="3" wrap="wrap">
          <Flex direction="column" gap="1" style={{ flex: '1 1 150px' }}>
            <Text weight="medium" size="2">Days before birthday</Text>
            <Select.Root
              value={String(selectedOffset)}
              onValueChange={(value) => onChange({ birthday_send_offset_days: Number(value) })}
            >
              <Select.Trigger style={{ width: '100%' }} />
              <Select.Content position="popper">
                {!hasPresetOffset && (
                  <Select.Item value={String(selectedOffset)}>{selectedOffset} days before</Select.Item>
                )}
                {BIRTHDAY_OFFSET_OPTIONS.map((option) => (
                  <Select.Item key={option.value} value={String(option.value)}>{option.label}</Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
          <Flex direction="column" gap="1" style={{ flex: '1 1 150px' }}>
            <Text weight="medium" size="2">Local send time</Text>
            <TextField.Root
              type="time"
              value={automation.birthday_send_time || ''}
              onChange={(event) => onChange({ birthday_send_time: event.target.value })}
            />
          </Flex>
          <Flex direction="column" gap="1" style={{ flex: '1 1 210px' }}>
            <Text weight="medium" size="2">Timezone</Text>
            <Select.Root
              value={automation.birthday_timezone || ''}
              onValueChange={(value) => onChange({ birthday_timezone: value })}
            >
              <Select.Trigger placeholder="Select a timezone" style={{ width: '100%' }} />
              <Select.Content position="popper">
                <Select.Group>
                  {automation.birthday_timezone && !hasTimezoneOption && (
                    <Select.Item value={automation.birthday_timezone}>{automation.birthday_timezone}</Select.Item>
                  )}
                  {timezoneOptions?.map((timezone) => (
                    <Select.Item key={timezone.value} value={timezone.value}>{timezone.label}</Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>
        <Text size="1" color="gray">Past occurrences are not sent on activation. Feb 29 birthdays send on Feb 28 in non-leap years.</Text>
        {!automation.birthday_timezone && (
          <Text size="1" color="red">Timezone selection is required before this Birthday Automation can be saved or activated.</Text>
        )}
      </>
    )}

    <Flex direction="column" gap="2">
      <Text weight="medium" size="2">Email eligibility</Text>
      <label>
        <Flex gap="2" align="center">
          <Checkbox
            checked={Boolean(automation.include_risky_emails)}
            onCheckedChange={(checked) => onChange({ include_risky_emails: checked === true })}
          />
          <Text size="2">Include risky email addresses</Text>
        </Flex>
      </label>
      <label>
        <Flex gap="2" align="center">
          <Checkbox
            checked={Boolean(automation.include_unknown_emails)}
            onCheckedChange={(checked) => onChange({ include_unknown_emails: checked === true })}
          />
          <Text size="2">Include terminal inconclusive email addresses</Text>
        </Flex>
      </label>
      <Text size="1" color="gray">Consent, suppression, pending checks, and retrying inconclusive results always remain blocked.</Text>
    </Flex>
  </Flex>
  );
};

const dialogBodyStyle = {
  flex: '1 1 auto',
  minHeight: 0,
  height: 'min(620px, calc(85vh - 170px))',
  overflow: 'hidden',
  alignItems: 'stretch'
};

const dialogFormPaneStyle = {
  flex: '1 1 0',
  minWidth: 360,
  minHeight: 0,
  overflowY: 'auto',
  paddingRight: 'var(--space-2)'
};

const dialogSeparatorStyle = {
  alignSelf: 'stretch',
  minHeight: '100%'
};

export default function MarketingAutomations() {
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const [selectedSegment, setSelectedSegment] = useState("all");
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [selectedWhatsappAutomation, setSelectedWhatsappAutomation] = useState(null);
  const [editedWhatsappAutomation, setEditedWhatsappAutomation] = useState(null);
  const [editedAutomation, setEditedAutomation] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isWhatsappDeleteDialogOpen, setIsWhatsappDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isWhatsappCreateDialogOpen, setIsWhatsappCreateDialogOpen] = useState(false);
  const [newAutomation, setNewAutomation] = useState({
    name: "",
    description: "",
    type: "email",
    sub_type: "welcome",
    trigger_type: "guest_visit",
    birthday_send_time: "09:00",
    birthday_send_offset_days: 0,
    birthday_timezone: "Europe/London",
    include_risky_emails: false,
    include_unknown_emails: false,
    associated_template_id: "",
    audience_level: "organisation",
    audience_brands: [],
    audience_locations: [],
    is_active: true,
    send_delay_minutes: 15,
    send_delay_unit: "minutes",
    repeat_enabled: false,
    repeat_cooldown_value: 1,
    repeat_cooldown_unit: "months",
    eligibility_filters: { match: "all", rules: [] },
  });
  const [newWhatsappAutomation, setNewWhatsappAutomation] = useState(() => buildWhatsappDraftAutomation());

  // Fetch automations based on selected segment
  const { data: automations, isLoading } = useMarketingAutomations(
    userProfile?.organisation_id,
    selectedSegment !== "all" && selectedSegment !== "sms" && selectedSegment !== "whatsapp" ? selectedSegment : null
  );

  // Fetch brands for audience selection
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);

  // Contact tags + locations for the eligibility filter builder
  const { contactTags } = useContactTags(userProfile?.organisation_id);
  const locationsList = useMemo(() => {
    if (!locationsData) return [];
    return locationsData.map((l) => ({ id: l.id, name: l.name }));
  }, [locationsData]);

  // Fetch email templates for template selection
  const { emailTemplates } = useMarketingEmailTemplates(userProfile?.organisation_id);
  const { whatsappTemplates } = useMarketingWhatsappTemplates(userProfile?.organisation_id);
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(userProfile?.organisation_id);
  const whatsappAccounts = useMemo(() => whatsappAccountsData?.data?.accounts || [], [whatsappAccountsData]);
  const hasWhatsappIntegration = whatsappAccounts.length > 0;
  const approvedWhatsappTemplates = useMemo(() => {
    return (whatsappTemplates || []).filter((template) => template.reviewStatus === "approved");
  }, [whatsappTemplates]);
  const hasApprovedWhatsappTemplates = approvedWhatsappTemplates.length > 0;
  const canCreateWhatsappAutomation = hasWhatsappIntegration && hasApprovedWhatsappTemplates;

  // Filter templates based on automation type and sub_type (purpose)
  const filteredTemplates = useMemo(() => {
    if (!emailTemplates || !editedAutomation) return [];
    // For now, only email templates are available
    if (editedAutomation.type !== 'email') return [];
    // Filter by purpose (sub_type maps to template purpose)
    return emailTemplates.filter(t => t.purpose === editedAutomation.sub_type && getTemplateSelectValue(t));
  }, [emailTemplates, editedAutomation]);

  // Filter templates for create dialog
  const filteredTemplatesForCreate = useMemo(() => {
    if (!emailTemplates) return [];
    // For now, only email templates are available
    if (newAutomation.type !== 'email') return [];
    // Filter by purpose (sub_type maps to template purpose)
    return emailTemplates.filter(t => t.purpose === newAutomation.sub_type && getTemplateSelectValue(t));
  }, [emailTemplates, newAutomation.type, newAutomation.sub_type]);

  const selectedCreateTemplate = useMemo(() => {
    const selectedTemplateValue = getSelectedTemplateValue(newAutomation.associated_template_id);
    return (emailTemplates || []).find((template) => getTemplateSelectValue(template) === selectedTemplateValue);
  }, [emailTemplates, newAutomation.associated_template_id]);

  const selectedEditTemplate = useMemo(() => {
    const selectedTemplateValue = getSelectedTemplateValue(editedAutomation?.associated_template_id);
    return (emailTemplates || []).find((template) => getTemplateSelectValue(template) === selectedTemplateValue);
  }, [emailTemplates, editedAutomation?.associated_template_id]);

  // Mutations
  const { mutate: updateAutomation, isPending: isUpdating } = useUpdateAutomation();
  const { mutate: deleteAutomation, isPending: isDeleting } = useDeleteAutomation();
  const { mutate: createAutomation, isPending: isCreating } = useCreateAutomation();

  // Initialize edited automation when selected
  useEffect(() => {
    if (selectedAutomation) {
      setEditedAutomation({
        ...selectedAutomation,
        audience_brands: selectedAutomation.audience_brands || [],
        audience_locations: selectedAutomation.audience_locations || [],
        eligibility_filters: {
          match: selectedAutomation.eligibility_filters?.match || "all",
          rules: selectedAutomation.eligibility_filters?.rules
            ? selectedAutomation.eligibility_filters.rules.map((r) => ({ ...r }))
            : [],
        },
      });
    } else {
      setEditedAutomation(null);
    }
  }, [selectedAutomation]);

  useEffect(() => {
    if (selectedWhatsappAutomation) {
      setEditedWhatsappAutomation(buildWhatsappDraftAutomation(selectedWhatsappAutomation));
      return;
    }

    setEditedWhatsappAutomation(null);
  }, [selectedWhatsappAutomation]);

  useEffect(() => {
    if (!isWhatsappCreateDialogOpen) {
      setNewWhatsappAutomation(buildWhatsappDraftAutomation());
    }
  }, [isWhatsappCreateDialogOpen]);

  useEffect(() => {
    if (newAutomation.trigger_type === 'birthday' && !newAutomation.birthday_timezone) {
      const inferred = inferAudienceTimezone(newAutomation, locationsData || []);
      if (inferred) setNewAutomation((current) => ({ ...current, birthday_timezone: inferred }));
    }
  }, [newAutomation, locationsData]);

  useEffect(() => {
    if (editedAutomation?.trigger_type === 'birthday' && !editedAutomation.birthday_timezone) {
      const inferred = inferAudienceTimezone(editedAutomation, locationsData || []);
      if (inferred) setEditedAutomation((current) => ({ ...current, birthday_timezone: inferred }));
    }
  }, [editedAutomation, locationsData]);

  // Check if there are changes
  const hasChanges = useMemo(() => {
    if (!selectedAutomation || !editedAutomation) return false;
    return (
      selectedAutomation.name !== editedAutomation.name ||
      selectedAutomation.description !== editedAutomation.description ||
      selectedAutomation.is_active !== editedAutomation.is_active ||
      selectedAutomation.trigger_type !== editedAutomation.trigger_type ||
      selectedAutomation.birthday_send_time !== editedAutomation.birthday_send_time ||
      selectedAutomation.birthday_send_offset_days !== editedAutomation.birthday_send_offset_days ||
      selectedAutomation.birthday_timezone !== editedAutomation.birthday_timezone ||
      selectedAutomation.include_risky_emails !== editedAutomation.include_risky_emails ||
      selectedAutomation.include_unknown_emails !== editedAutomation.include_unknown_emails ||
      selectedAutomation.associated_template_id !== editedAutomation.associated_template_id ||
      selectedAutomation.audience_level !== editedAutomation.audience_level ||
      selectedAutomation.send_delay_minutes !== editedAutomation.send_delay_minutes ||
      selectedAutomation.send_delay_unit !== editedAutomation.send_delay_unit ||
      selectedAutomation.repeat_enabled !== editedAutomation.repeat_enabled ||
      selectedAutomation.repeat_cooldown_value !== editedAutomation.repeat_cooldown_value ||
      selectedAutomation.repeat_cooldown_unit !== editedAutomation.repeat_cooldown_unit ||
      JSON.stringify(selectedAutomation.audience_brands) !== JSON.stringify(editedAutomation.audience_brands) ||
      JSON.stringify(selectedAutomation.audience_locations) !== JSON.stringify(editedAutomation.audience_locations) ||
      JSON.stringify(buildEligibilityPayload(selectedAutomation.eligibility_filters)) !== JSON.stringify(buildEligibilityPayload(editedAutomation.eligibility_filters))
    );
  }, [selectedAutomation, editedAutomation]);

  const hasWhatsappChanges = useMemo(() => {
    if (!selectedWhatsappAutomation || !editedWhatsappAutomation) return false;
    return (
      selectedWhatsappAutomation.name !== editedWhatsappAutomation.name ||
      selectedWhatsappAutomation.description !== editedWhatsappAutomation.description ||
      selectedWhatsappAutomation.trigger_type !== editedWhatsappAutomation.trigger_type ||
      selectedWhatsappAutomation.birthday_send_time !== editedWhatsappAutomation.birthday_send_time ||
      selectedWhatsappAutomation.birthday_send_offset_days !== editedWhatsappAutomation.birthday_send_offset_days ||
      selectedWhatsappAutomation.birthday_timezone !== editedWhatsappAutomation.birthday_timezone ||
      selectedWhatsappAutomation.associated_template_id !== editedWhatsappAutomation.associated_template_id ||
      selectedWhatsappAutomation.whatsapp_phone_number_id !== editedWhatsappAutomation.whatsapp_phone_number_id ||
      selectedWhatsappAutomation.audience_level !== editedWhatsappAutomation.audience_level ||
      selectedWhatsappAutomation.send_delay_minutes !== editedWhatsappAutomation.send_delay_minutes ||
      selectedWhatsappAutomation.send_delay_unit !== editedWhatsappAutomation.send_delay_unit ||
      selectedWhatsappAutomation.repeat_enabled !== editedWhatsappAutomation.repeat_enabled ||
      selectedWhatsappAutomation.repeat_cooldown_value !== editedWhatsappAutomation.repeat_cooldown_value ||
      selectedWhatsappAutomation.repeat_cooldown_unit !== editedWhatsappAutomation.repeat_cooldown_unit ||
      selectedWhatsappAutomation.is_active !== editedWhatsappAutomation.is_active ||
      JSON.stringify(selectedWhatsappAutomation.audience_brands || []) !== JSON.stringify(editedWhatsappAutomation.audience_brands || []) ||
      JSON.stringify(selectedWhatsappAutomation.audience_locations || []) !== JSON.stringify(editedWhatsappAutomation.audience_locations || []) ||
      JSON.stringify(buildEligibilityPayload(selectedWhatsappAutomation.eligibility_filters)) !== JSON.stringify(buildEligibilityPayload(editedWhatsappAutomation.eligibility_filters))
    );
  }, [selectedWhatsappAutomation, editedWhatsappAutomation]);

  // Filter automations based on selected segment
  const filteredAutomations = useMemo(() => {
    if (!automations) return [];
    if (selectedSegment === "all") return automations;
    return automations.filter((automation) => automation.type === selectedSegment);
  }, [automations, selectedSegment]);

  // Group automations by sub_type and sort by created_at within each group
  const groupedAutomations = useMemo(() => {
    if (!filteredAutomations || filteredAutomations.length === 0) return {};
    
    const groups = {};
    SECTION_ORDER.forEach(subType => {
      const automationsForSubType = filteredAutomations
        .filter(a => getAutomationSectionKey(a) === subType)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      if (automationsForSubType.length > 0) {
        groups[subType] = automationsForSubType;
      }
    });
    return groups;
  }, [filteredAutomations]);

  const hasAutomations = Object.keys(groupedAutomations).length > 0;

  // Check if there are any email templates available
  const hasEmailTemplates = useMemo(() => {
    return emailTemplates && emailTemplates.length > 0;
  }, [emailTemplates]);

  // Handle update automation
  const handleUpdateAutomation = () => {
    updateAutomation({
      automationId: selectedAutomation.automation_id,
      data: {
        name: editedAutomation.name,
        description: editedAutomation.description,
        is_active: editedAutomation.is_active,
        associated_template_id: editedAutomation.associated_template_id,
        trigger_type: editedAutomation.trigger_type,
        birthday_send_time: editedAutomation.trigger_type === 'birthday' ? editedAutomation.birthday_send_time : null,
        birthday_send_offset_days: editedAutomation.trigger_type === 'birthday' ? Number(editedAutomation.birthday_send_offset_days || 0) : 0,
        birthday_timezone: editedAutomation.trigger_type === 'birthday' ? editedAutomation.birthday_timezone : null,
        include_risky_emails: Boolean(editedAutomation.include_risky_emails),
        include_unknown_emails: Boolean(editedAutomation.include_unknown_emails),
        audience_level: editedAutomation.audience_level,
        audience_brands: editedAutomation.audience_brands,
        audience_locations: editedAutomation.audience_locations,
        send_delay_minutes: editedAutomation.trigger_type === 'birthday' ? 0 : editedAutomation.send_delay_minutes,
        send_delay_unit: editedAutomation.trigger_type === 'birthday' ? 'minutes' : editedAutomation.send_delay_unit,
        repeat_enabled: editedAutomation.trigger_type === 'birthday' ? false : editedAutomation.repeat_enabled,
        repeat_cooldown_value: editedAutomation.trigger_type !== 'birthday' && editedAutomation.repeat_enabled ? editedAutomation.repeat_cooldown_value : null,
        repeat_cooldown_unit: editedAutomation.trigger_type !== 'birthday' && editedAutomation.repeat_enabled ? editedAutomation.repeat_cooldown_unit : null,
        eligibility_filters: buildEligibilityPayload(editedAutomation.eligibility_filters),
      }
    }, {
      onSuccess: () => {
        setSelectedAutomation(null);
      }
    });
  };

  // Handle delete automation
  const handleDeleteAutomation = () => {
    deleteAutomation(selectedAutomation.automation_id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setSelectedAutomation(null);
      }
    });
  };

  const handleUpdateWhatsappAutomation = () => {
    updateAutomation({
      automationId: selectedWhatsappAutomation.automation_id,
      data: buildWhatsappAutomationPayload(editedWhatsappAutomation, userProfile.organisation_id),
    }, {
      onSuccess: () => {
        setSelectedWhatsappAutomation(null);
      }
    });
  };

  const handleDeleteWhatsappAutomation = () => {
    deleteAutomation(selectedWhatsappAutomation.automation_id, {
      onSuccess: () => {
        setIsWhatsappDeleteDialogOpen(false);
        setSelectedWhatsappAutomation(null);
      }
    });
  };

  // Handle create automation
  const handleCreateAutomation = () => {
    const payload = {
      ...newAutomation,
      organisation: userProfile.organisation_id,
      send_delay_minutes: newAutomation.trigger_type === 'birthday' ? 0 : newAutomation.send_delay_minutes,
      send_delay_unit: newAutomation.trigger_type === 'birthday' ? 'minutes' : newAutomation.send_delay_unit,
      repeat_enabled: newAutomation.trigger_type === 'birthday' ? false : newAutomation.repeat_enabled,
      repeat_cooldown_value: newAutomation.trigger_type !== 'birthday' && newAutomation.repeat_enabled ? newAutomation.repeat_cooldown_value : null,
      repeat_cooldown_unit: newAutomation.trigger_type !== 'birthday' && newAutomation.repeat_enabled ? newAutomation.repeat_cooldown_unit : null,
      eligibility_filters: buildEligibilityPayload(newAutomation.eligibility_filters),
    };
    
    createAutomation(payload, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        setNewAutomation({
          name: "",
          description: "",
          type: "email",
          sub_type: "welcome",
          trigger_type: "guest_visit",
          birthday_send_time: "09:00",
          birthday_send_offset_days: 0,
          birthday_timezone: "Europe/London",
          include_risky_emails: false,
          include_unknown_emails: false,
          associated_template_id: "",
          audience_level: "organisation",
          audience_brands: [],
          audience_locations: [],
          is_active: true,
          send_delay_minutes: 15,
          send_delay_unit: "minutes",
          repeat_enabled: false,
          repeat_cooldown_value: 1,
          repeat_cooldown_unit: "months",
          eligibility_filters: { match: "all", rules: [] },
        });
      }
    });
  };

  const handleCreateWhatsappAutomation = () => {
    createAutomation(buildWhatsappAutomationPayload(newWhatsappAutomation, userProfile.organisation_id), {
      onSuccess: () => {
        setIsWhatsappCreateDialogOpen(false);
      }
    });
  };

  // Handle brand checkbox change for create
  const handleNewBrandChange = (brandId, checked) => {
    if (checked) {
      setNewAutomation({
        ...newAutomation,
        audience_brands: [...newAutomation.audience_brands, brandId]
      });
    } else {
      setNewAutomation({
        ...newAutomation,
        audience_brands: newAutomation.audience_brands.filter(id => id !== brandId)
      });
    }
  };

  // Handle location checkbox change for create
  const handleNewLocationChange = (locationId, checked) => {
    if (checked) {
      setNewAutomation({
        ...newAutomation,
        audience_locations: [...newAutomation.audience_locations, locationId]
      });
    } else {
      setNewAutomation({
        ...newAutomation,
        audience_locations: newAutomation.audience_locations.filter(id => id !== locationId)
      });
    }
  };

  // Handle brand checkbox change
  const handleBrandChange = (brandId, checked) => {
    if (checked) {
      setEditedAutomation({
        ...editedAutomation,
        audience_brands: [...editedAutomation.audience_brands, brandId]
      });
    } else {
      setEditedAutomation({
        ...editedAutomation,
        audience_brands: editedAutomation.audience_brands.filter(id => id !== brandId)
      });
    }
  };

  // Handle location checkbox change
  const handleLocationChange = (locationId, checked) => {
    if (checked) {
      setEditedAutomation({
        ...editedAutomation,
        audience_locations: [...editedAutomation.audience_locations, locationId]
      });
    } else {
      setEditedAutomation({
        ...editedAutomation,
        audience_locations: editedAutomation.audience_locations.filter(id => id !== locationId)
      });
    }
  };

  const handleAutomationCardClick = (automation) => {
    if (automation.type === "whatsapp") {
      setSelectedAutomation(null);
      setSelectedWhatsappAutomation(automation);
      return;
    }

    setSelectedAutomation(automation);
  };

  if (selectedSegment === "whatsapp") {
    return (
      <MarketingWhatsappAutomations
        selectedSegment={selectedSegment}
        onSelectedSegmentChange={setSelectedSegment}
      />
    );
  }

  return (
    <>
      {/* Loading Progress Bar */}
      {isLoading && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
      {/* Header */}
      <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
        {/* Filter Segmented Control */}
        <SegmentedControl.Root value={selectedSegment} onValueChange={setSelectedSegment}>
          <SegmentedControl.Item value="all">All</SegmentedControl.Item>
          <SegmentedControl.Item value="email">
            <Flex gap="1" align="center">
              <EnvelopeClosedIcon />
              <Text>Email</Text>
            </Flex>
          </SegmentedControl.Item>
          <SegmentedControl.Item value="sms">
            <Flex gap="1" align="center">
              <MobileIcon />
              <Text>SMS</Text>
            </Flex>
          </SegmentedControl.Item>
          <SegmentedControl.Item value="whatsapp">
            <Flex gap="1" align="center">
              <ChatBubbleIcon />
              <Text>WhatsApp</Text>
            </Flex>
          </SegmentedControl.Item>
        </SegmentedControl.Root>

        {selectedSegment === "all" ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button variant="soft" disabled={isCreating}>
                <PlusIcon width="16" height="16" />
                Create automation
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item onClick={() => setIsCreateDialogOpen(true)} disabled={!hasEmailTemplates}>
                <Flex gap="2" align="center">
                  <EnvelopeClosedIcon />
                  <Text>Email automation</Text>
                </Flex>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={(event) => {
                  if (!canCreateWhatsappAutomation) {
                    event.preventDefault();
                    return;
                  }

                  setIsWhatsappCreateDialogOpen(true);
                }}
                style={!canCreateWhatsappAutomation ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              >
                <Tooltip content={!hasWhatsappIntegration ? WHATSAPP_AUTOMATION_INTEGRATION_REQUIRED_MESSAGE : !hasApprovedWhatsappTemplates ? "Create an approved WhatsApp template to enable WhatsApp automations" : "Create WhatsApp automation"}>
                <Flex gap="2" align="center">
                  <ChatBubbleIcon />
                  <Text>WhatsApp automation</Text>
                </Flex>
                </Tooltip>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : !hasEmailTemplates && selectedSegment !== "sms" && selectedSegment !== "whatsapp" ? (
          <Tooltip content="Create at least one template to enable email automations">
            <Button 
              variant="soft" 
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={isCreating || selectedSegment === "sms" || selectedSegment === "whatsapp" || !hasEmailTemplates}
            >
              <PlusIcon width="16" height="16" />
              Create automation
            </Button>
          </Tooltip>
        ) : (
          <Button 
            variant="soft" 
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={isCreating || selectedSegment === "sms" || selectedSegment === "whatsapp" || !hasEmailTemplates}
          >
            <PlusIcon width="16" height="16" />
            Create automation
          </Button>
        )}
      </Flex>
      {/* Automations Grid */}
      {selectedSegment === "sms" || selectedSegment === "whatsapp" ? (
        <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
          border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%'}}>
          <Text color="gray">SMS and Whatsapp features coming soon.</Text>
        </Flex>
      ) : (
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: 'auto', height: '100%',
        flex: "1 0 0" }}>
        { !hasAutomations ? (
          <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
            border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%', padding: 'var(--space-6)'}}>
            <Text color="gray">No automations found. Create your first automation to get started.</Text>
          </Flex>
        ) : (
          SECTION_ORDER.map((subType, index) => {
            const automationsInSection = groupedAutomations[subType];
            if (!automationsInSection || automationsInSection.length === 0) return null;
            
            return (
              <React.Fragment key={subType}>
                {index > 0 && Object.keys(groupedAutomations).indexOf(subType) > 0 && (
                  <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}} />
                )}
                <Flex direction="column" gap="4" style={{ alignSelf: 'stretch'}}>
                  <Text size="3" weight="medium">{formatSubType(subType)}</Text>
                  <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
                    {automationsInSection.map((automation) => (
                      <Card key={automation.automation_id} style={{ cursor: "pointer", display: "flex", flexDirection: "column" }} 
                        onClick={() => handleAutomationCardClick(automation)}>
                        <Flex direction="column" gap="4" justify="between" height="100%">
                          <Flex direction="column" gap="2">
                            {/* Header row with type icon and status */}
                            <Flex justify="between" align="center" style={{ alignSelf: 'stretch'}}>
                              <Flex gap="2" align="center">
                                {automation.type === "whatsapp" ? <ChatBubbleIcon /> : <TimerIcon />}
                                <Text weight="bold" size="3">{automation.name}</Text>
                              </Flex>
                              <Badge color={automation.is_active ? "green" : "gray"} variant="soft">
                                {automation.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </Flex>
                            
                            {/* Description */}
                            {automation.description && (
                              <Text color="gray" size="2" weight="medium"
                                style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", 
                                WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                {automation.description}
                              </Text>
                            )}
                            {selectedSegment === "all" && (
                              <Text color="gray" size="1">
                                {automation.type === "whatsapp" ? "WhatsApp" : "Email"}
                              </Text>
                            )}
                            <Flex direction="row" gap="2" align="center">
                              <AutomationTriggerBadge triggerType={automation.trigger_type} />
                              <Text color="gray" size="1" >
                                {automation.trigger_type === 'birthday'
                                  ? `(${automation.birthday_send_offset_days || 0} days before)`
                                  : "(Runs after an eligible guest visit)"}
                              </Text>
                            </Flex>
                            <Flex direction="row" gap="2" justify="between" style={{ width: "100%" }}>
                              <Flex direction="column" gap="1" style={{ flex: "1 1 0", minWidth: 0 }}>
                                <Text size="1" color="gray" weight="medium">Created on</Text>
                                <AutomationTimestamp value={automation.created_at} />
                              </Flex>
                              <Flex direction="column" gap="1" style={{ flex: "1 1 0", minWidth: 0 }}>
                                <Text size="1" color="gray" weight="medium">Updated on</Text>
                                <AutomationTimestamp value={automation.updated_at} />
                              </Flex>
                            </Flex>
                          </Flex>                  
                          {/* Audience info */}
                          <Flex gap="2" direction="column">
                            <Separator orientation="horizontal" size="4" minHeight="1px" />
                            <Flex gap="2" align="center">
                              <PersonIcon />
                              <Text size="1" color="gray" weight="medium">
                                {formatAudienceLevel(automation.audience_level)} level
                                {automation.target_locations_count > 0 && (
                                  <> · {automation.target_locations_count} location{automation.target_locations_count !== 1 ? 's' : ''}</>
                                )}
                              </Text>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Card>
                    ))}
                  </Grid>
                </Flex>
              </React.Fragment>
            );
          })
        )}
      </Flex>
      )}

      {/* Create Email/SMS/WhatsApp Automation Dialog */}
      <Dialog.Root open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <Dialog.Content style={{ height: '100%', width: 'min(1120px, calc(100vw - 32px))', maxWidth: 1120, display: 'flex', flexDirection: 'column' }}>
          <Dialog.Title>Create Automation</Dialog.Title>
          <Dialog.Description size="2" mb="4" color="gray">
            Set up a new automation for your marketing campaigns.
          </Dialog.Description>

          <Flex gap="4" style={dialogBodyStyle}>
            <Flex direction="column" gap="4" style={dialogFormPaneStyle}>
              {/* Name */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch', paddingLeft: '1px' }}>
                <Text weight="medium" size="2">Name</Text>
                <TextField.Root
                  value={newAutomation.name} 
                  onChange={(e) => setNewAutomation({...newAutomation, name: e.target.value})}
                  placeholder="Enter automation name"
                />
              </Flex>

              {/* Description */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch', paddingLeft: '1px' }}>
                <Text weight="medium" size="2">Description (recommended)</Text>
                <TextArea 
                  value={newAutomation.description} 
                  onChange={(e) => setNewAutomation({...newAutomation, description: e.target.value})}
                  placeholder="This description will help you identify the automation's purpose."
                  style={{ height: 80 }}
                />
              </Flex>

            {/* Type & Sub-type */}
            <Flex gap="4">
              <Flex direction="column" style={{ flex: 1 }}>
                <Text weight="medium" size="2">Type</Text>
                <Select.Root 
                  value={newAutomation.type} 
                  onValueChange={(val) => setNewAutomation({
                    ...newAutomation, 
                    type: val,
                    associated_template_id: "" // Clear template when type changes
                  })}
                >
                  <Select.Trigger style={{ width: '100%', marginTop: '4px' }} />
                  <Select.Content>
                    <Select.Item value="email">
                      <Flex align="center" gap="2">
                        <EnvelopeClosedIcon />
                        Email
                      </Flex>
                    </Select.Item>
                    <Select.Item value="sms" disabled>
                      <Flex align="center" gap="1">
                        <MobileIcon />
                        SMS
                        <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                      </Flex>
                    </Select.Item>
                    <Select.Item value="whatsapp" disabled>
                      <Flex align="center" gap="1">
                        <ChatBubbleIcon />
                        WhatsApp
                        <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                      </Flex>
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
              <Flex direction="column" style={{ flex: 1 }}>
                <Text weight="medium" size="2">Sub-type</Text>
                <Select.Root 
                  value={newAutomation.sub_type} 
                  onValueChange={(val) => setNewAutomation({
                    ...newAutomation, 
                    sub_type: val,
                    associated_template_id: "" // Clear template when sub_type changes
                  })}
                >
                  <Select.Trigger style={{ width: '100%', marginTop: '4px' }} />
                  <Select.Content>
                    <Select.Item value="welcome">Welcome</Select.Item>
                    <Select.Item value="newsletter">Newsletter</Select.Item>
                    <Select.Item value="review">Review</Select.Item>
                    <Select.Item value="promotion" disabled>
                      <Flex gap="1" align="center">
                        Promotion/Offers
                        <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                      </Flex>
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
            </Flex>

              <EmailTriggerAndEligibilityFields
                automation={newAutomation}
                onChange={(patch) => setNewAutomation((current) => ({ ...current, ...patch }))}
              />

              {/* Associated Template */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Text weight="medium" size="2">Associated template</Text>
                {newAutomation.type === 'email' ? (
                  <Select.Root 
                    value={getSelectedTemplateValue(newAutomation.associated_template_id)} 
                    onValueChange={(val) => setNewAutomation({...newAutomation, associated_template_id: val})}
                  >
                    <Select.Trigger 
                      style={{ width: '100%', marginTop: '4px' }} 
                      placeholder="Select a template"
                    />
                    <Select.Content>
                      {filteredTemplatesForCreate.length > 0 ? (
                        filteredTemplatesForCreate.map((template) => {
                          const templateValue = getTemplateSelectValue(template);
                          return (
                            <Select.Item key={templateValue} value={templateValue}>
                              {template.name}
                            </Select.Item>
                          );
                        })
                      ) : (
                        <Select.Item value={EMPTY_TEMPLATE_OPTION_VALUE} disabled>
                          No templates available for this sub-type
                        </Select.Item>
                      )}
                    </Select.Content>
                  </Select.Root>
                ) : (
                  <Text size="2" color="gray" mt="1">
                    SMS and WhatsApp templates coming soon
                  </Text>
                )}
              </Flex>

              {/* Delay Configuration */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Text weight="medium" size="2">Send delay</Text>
                <Select.Root 
                  value={`${newAutomation.send_delay_minutes}-${newAutomation.send_delay_unit}`}
                  onValueChange={(val) => {
                    const [minutes, unit] = val.split('-');
                    setNewAutomation({
                      ...newAutomation, 
                      send_delay_minutes: parseInt(minutes),
                      send_delay_unit: unit
                    });
                  }}
                >
                  <Select.Trigger style={{ width: '100%', marginTop: '4px' }} />
                  <Select.Content>
                    <Select.Item value="0-minutes">Instant</Select.Item>
                    <Select.Item value="15-minutes">15 minutes</Select.Item>
                    <Select.Item value="30-minutes">30 minutes</Select.Item>
                    <Select.Item value="1-hours">1 hour</Select.Item>
                    <Select.Item value="2-hours">2 hours</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>

              {/* Repeat Configuration */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Flex align="center" justify="between">
                  <Text weight="medium" size="2">Repeat automation</Text>
                  <Switch 
                    checked={newAutomation.repeat_enabled}
                    onCheckedChange={(checked) => setNewAutomation({...newAutomation, repeat_enabled: checked})}
                  />
                </Flex>
                {newAutomation.repeat_enabled && (
                  <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                    <Text size="1" color="gray">Cooldown period before sending again</Text>
                    <Select.Root 
                      value={`${newAutomation.repeat_cooldown_value}-${newAutomation.repeat_cooldown_unit}`}
                      onValueChange={(val) => {
                        const [value, unit] = val.split('-');
                        setNewAutomation({
                          ...newAutomation, 
                          repeat_cooldown_value: parseInt(value),
                          repeat_cooldown_unit: unit
                        });
                      }}
                    >
                      <Select.Trigger style={{ width: '100%' }} />
                      <Select.Content>
                        <Select.Item value="1-months">1 month</Select.Item>
                        <Select.Item value="2-months">2 months</Select.Item>
                        <Select.Item value="3-months">3 months</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Flex>
                )}
              </Flex>

              {/* Active Status */}
              <Flex align="center" justify="between">
                <Text weight="medium" size="2">Active</Text>
                <Switch 
                  checked={newAutomation.is_active}
                  onCheckedChange={(checked) => setNewAutomation({...newAutomation, is_active: checked})}
                />
              </Flex>

              {/* Audience Level */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Text weight="medium" size="2">Audience level</Text>
                <Select.Root 
                  value={newAutomation.audience_level} 
                  onValueChange={(val) => setNewAutomation({
                    ...newAutomation, 
                    audience_level: val,
                    // Clear selections when changing level
                    audience_brands: val === 'brand' ? newAutomation.audience_brands : [],
                    audience_locations: val === 'location' ? newAutomation.audience_locations : []
                  })}
                >
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Item value="organisation">Organisation (All locations)</Select.Item>
                    <Select.Item value="brand">Brand (Select brands)</Select.Item>
                    <Select.Item value="location">Location (Select locations)</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>

              {/* Brand Selection (when audience_level is 'brand') */}
              {newAutomation.audience_level === 'brand' && brandsData && (
                <Box>
                  <Text weight="bold" size="2">Select Brands</Text>
                  <Flex direction="column" gap="2" mt="2" style={{ maxHeight: 150, overflowY: 'auto' }}>
                    {brandsData.filter(b => !b.is_default).map((brand) => (
                      <Flex key={brand.id} align="center" gap="2">
                        <Checkbox 
                          checked={newAutomation.audience_brands.includes(brand.id)}
                          onCheckedChange={(checked) => handleNewBrandChange(brand.id, checked)}
                        />
                        <Text size="2">{brand.name}</Text>
                      </Flex>
                    ))}
                    {brandsData.filter(b => !b.is_default).length === 0 && (
                      <Text size="2" color="gray">No brands available</Text>
                    )}
                  </Flex>
                </Box>
              )}

              {/* Location Selection (when audience_level is 'location') */}
              {newAutomation.audience_level === 'location' && locationsData && (
                <Box>
                  <Text weight="medium" size="2">Select Locations</Text>
                  <Grid columns="3" gap="3" mt="2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {locationsData.map((location) => (
                      <Flex key={location.id} align="center" gap="2">
                        <Checkbox 
                          checked={newAutomation.audience_locations.includes(location.id)}
                          onCheckedChange={(checked) => handleNewLocationChange(location.id, checked)}
                        />
                        <Text size="2" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {location.name}
                        </Text>
                      </Flex>
                    ))}
                  </Grid>
                  {locationsData.length === 0 && (
                    <Text size="2" color="gray" mt="2">No locations available</Text>
                  )}
                </Box>
              )}

              {/* Eligibility filters (optional) */}
              <Flex direction="column" gap="2" style={{ alignSelf: 'stretch' }}>
                <Flex direction="column" gap="1">
                  <Text weight="medium" size="2">Eligibility filters (optional)</Text>
                  <Text size="1" color="gray">
                    Limit this automation to contacts matching attribute rules (e.g. age group, tags).
                    Leave empty to include every contact in the selected audience.
                  </Text>
                </Flex>
                <ContactFilterBuilder
                  matchMode={newAutomation.eligibility_filters?.match || "all"}
                  onMatchModeChange={(val) => setNewAutomation({
                    ...newAutomation,
                    eligibility_filters: { ...newAutomation.eligibility_filters, match: val },
                  })}
                  rules={newAutomation.eligibility_filters?.rules || []}
                  onRulesChange={(rules) => setNewAutomation({
                    ...newAutomation,
                    eligibility_filters: { ...newAutomation.eligibility_filters, rules },
                  })}
                  locations={locationsList}
                  tags={contactTags || []}
                />
              </Flex>
            </Flex>
            <Separator orientation="vertical" size="4" style={dialogSeparatorStyle} />
            <TemplatePreviewPanel template={selectedCreateTemplate} />
          </Flex>

          <Flex gap="3" mt="5" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button 
              onClick={handleCreateAutomation} 
              disabled={
                isCreating || !newAutomation.name ||
                (newAutomation.type === 'email' && !newAutomation.associated_template_id) ||
                (newAutomation.trigger_type === 'birthday' && (!newAutomation.birthday_send_time || !newAutomation.birthday_timezone))
              }
              loading={isCreating}
            >
              Create automation
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      
      {/* WhatsApp Automation Create Dialog */}
      <Dialog.Root open={isWhatsappCreateDialogOpen} onOpenChange={setIsWhatsappCreateDialogOpen}>
        <Dialog.Content style={{ height: '100%', width: 'min(1120px, calc(100vw - 32px))', maxWidth: 1120, display: 'flex', flexDirection: 'column' }}>
          <Dialog.Title>Create Automation</Dialog.Title>
          <Dialog.Description size="2" mb="4" color="gray">
            Set up a new automation for your marketing campaigns.
          </Dialog.Description>

          <WhatsAppAutomationFields
            automation={newWhatsappAutomation}
            setAutomation={setNewWhatsappAutomation}
            templates={approvedWhatsappTemplates}
            brands={brandsData || []}
            locations={locationsData || []}
            titleSuffix="automation"
            organisationId={userProfile?.organisation_id}
            contactTags={contactTags || []}
          />

          <Flex gap="3" mt="5" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button
              onClick={handleCreateWhatsappAutomation}
              disabled={
                isCreating ||
                !newWhatsappAutomation.name.trim() ||
                !newWhatsappAutomation.associated_template_id ||
                !newWhatsappAutomation.whatsapp_phone_number_id ||
                (newWhatsappAutomation.trigger_type === "birthday" && (!newWhatsappAutomation.birthday_send_time || !newWhatsappAutomation.birthday_timezone))
              }
              loading={isCreating}
            >
              Create automation
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Edit Email/SMS/WhatsApp Automation Dialog */}
      <Dialog.Root open={!!selectedAutomation} onOpenChange={(open) => !open && setSelectedAutomation(null)}>
        <Dialog.Content style={{ height: '100%', width: 'min(1120px, calc(100vw - 32px))', maxWidth: 1120, display: 'flex', flexDirection: 'column' }}>
          <Dialog.Title>Edit Automation</Dialog.Title>
          <Dialog.Description size="2" mb="4" color="gray">
            Modify the automation settings below.
          </Dialog.Description>

          <Flex gap="4" px="1" style={dialogBodyStyle}>
            <Flex direction="column" gap="4" style={dialogFormPaneStyle}>
              {/* Name */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch', paddingLeft: '1px' }}>
                <Text as="label" weight="medium" size="2">Name</Text>
                <TextField.Root
                  value={editedAutomation?.name || ""} 
                  onChange={(e) => setEditedAutomation({...editedAutomation, name: e.target.value})}
                />
              </Flex>

              {/* Description */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch', paddingLeft: '1px' }}>
                <Text weight="medium" size="2">Description (recommended)</Text>
                <TextArea 
                  value={editedAutomation?.description || ""} 
                  onChange={(e) => setEditedAutomation({...editedAutomation, description: e.target.value})}
                  placeholder="This description will help you identify the automation's purpose."
                  style={{ height: 80 }}
                />
              </Flex>

              {/* Type & Sub-type (read-only) */}
              <Flex gap="4" justify="between">
                <Flex align="center" gap="2">
                  <Text weight="medium" size="2">Type :</Text>
                  <Flex align="center" gap="2">
                    {editedAutomation?.type === 'email' && <EnvelopeClosedIcon />}
                    {editedAutomation?.type === 'sms' && <MobileIcon />}
                    {editedAutomation?.type === 'whatsapp' && <ChatBubbleIcon />}
                    <Text size="2">{formatAudienceLevel(editedAutomation?.type || '')}</Text>
                  </Flex>
                </Flex>
                <Flex align="center" gap="2">
                  <Text weight="medium" size="2">Sub-type :</Text>
                  { editedAutomation?.sub_type === 'welcome' && <Badge color="crimson" variant="soft">{formatSubType(editedAutomation?.sub_type)}</Badge>}
                  { editedAutomation?.sub_type === 'newsletter' && <Badge color="blue" variant="soft">{formatSubType(editedAutomation?.sub_type)}</Badge>}
                  { editedAutomation?.sub_type === 'review' && <Badge color="purple" variant="soft">{formatSubType(editedAutomation?.sub_type)}</Badge> }
                  { editedAutomation?.sub_type === 'promotion' && <Badge color="orange" variant="soft">{formatSubType(editedAutomation?.sub_type)}</Badge> }
                </Flex>
              </Flex>

              {editedAutomation && (
                <EmailTriggerAndEligibilityFields
                  automation={editedAutomation}
                  onChange={(patch) => setEditedAutomation((current) => ({ ...current, ...patch }))}
                />
              )}

              {/* Associated Template */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Text weight="medium" size="2">Associated template</Text>
                {editedAutomation?.type === 'email' ? (
                  <Select.Root 
                    value={getSelectedTemplateValue(editedAutomation?.associated_template_id)} 
                    onValueChange={(val) => setEditedAutomation({...editedAutomation, associated_template_id: val})}
                  >
                    <Select.Trigger 
                      style={{ width: '100%' }} 
                      placeholder="Select a template"
                    />
                    <Select.Content>
                      {filteredTemplates.length > 0 ? (
                        filteredTemplates.map((template) => {
                          const templateValue = getTemplateSelectValue(template);
                          return (
                            <Select.Item key={templateValue} value={templateValue}>
                              {template.name}
                            </Select.Item>
                          );
                        })
                      ) : (
                        <Select.Item value={EMPTY_TEMPLATE_OPTION_VALUE} disabled>
                          No templates available for this sub-type
                        </Select.Item>
                      )}
                    </Select.Content>
                  </Select.Root>
                ) : (
                  <Text size="2" color="gray">
                    SMS and WhatsApp templates coming soon
                  </Text>
                )}
              </Flex>

              {/* Delay Configuration */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Text weight="medium" size="2">Send delay</Text>
                <Select.Root 
                  value={`${editedAutomation?.send_delay_minutes ?? 15}-${editedAutomation?.send_delay_unit ?? 'minutes'}`}
                  onValueChange={(val) => {
                    const [minutes, unit] = val.split('-');
                    setEditedAutomation({
                      ...editedAutomation, 
                      send_delay_minutes: parseInt(minutes),
                      send_delay_unit: unit
                    });
                  }}
                >
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Item value="0-minutes">Instant</Select.Item>
                    <Select.Item value="15-minutes">15 minutes</Select.Item>
                    <Select.Item value="30-minutes">30 minutes</Select.Item>
                    <Select.Item value="1-hours">1 hour</Select.Item>
                    <Select.Item value="2-hours">2 hours</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>

              {/* Repeat Configuration */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Flex align="center" justify="between">
                  <Text weight="medium" size="2">Repeat automation</Text>
                  <Switch 
                    checked={editedAutomation?.repeat_enabled || false}
                    onCheckedChange={(checked) => setEditedAutomation({...editedAutomation, repeat_enabled: checked})}
                  />
                </Flex>
                {editedAutomation?.repeat_enabled && (
                  <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                    <Text size="1" color="gray">Cooldown period before sending again</Text>
                    <Select.Root 
                      value={`${editedAutomation?.repeat_cooldown_value ?? 1}-${editedAutomation?.repeat_cooldown_unit ?? 'months'}`}
                      onValueChange={(val) => {
                        const [value, unit] = val.split('-');
                        setEditedAutomation({
                          ...editedAutomation, 
                          repeat_cooldown_value: parseInt(value),
                          repeat_cooldown_unit: unit
                        });
                      }}
                    >
                      <Select.Trigger style={{ width: '100%' }} />
                      <Select.Content>
                        <Select.Item value="1-months">1 month</Select.Item>
                        <Select.Item value="2-months">2 months</Select.Item>
                        <Select.Item value="3-months">3 months</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Flex>
                )}
              </Flex>

              {/* Active Status */}
              <Flex align="center" justify="between">
                <Text weight="medium" size="2">Active</Text>
                <Switch 
                  checked={editedAutomation?.is_active || false}
                  onCheckedChange={(checked) => setEditedAutomation({...editedAutomation, is_active: checked})}
                />
              </Flex>

              {/* Audience Level */}
              <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                <Text weight="medium" size="2">Audience level</Text>
                <Select.Root 
                  value={editedAutomation?.audience_level || 'organisation'} 
                  onValueChange={(val) => setEditedAutomation({
                    ...editedAutomation, 
                    audience_level: val,
                    // Clear selections when changing level
                    audience_brands: val === 'brand' ? editedAutomation.audience_brands : [],
                    audience_locations: val === 'location' ? editedAutomation.audience_locations : []
                  })}
                >
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Item value="organisation">Organisation (All locations)</Select.Item>
                    <Select.Item value="brand">Brand (Select brands)</Select.Item>
                    <Select.Item value="location">Location (Select locations)</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>

              {/* Brand Selection (when audience_level is 'brand') */}
              {editedAutomation?.audience_level === 'brand' && brandsData && (
                <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                  <Text weight="medium" size="2">Select Brands</Text>
                  <Flex direction="column" gap="2" mt="2" style={{ maxHeight: 150, overflowY: 'auto' }}>
                    {brandsData.filter(b => !b.is_default).map((brand) => (
                      <Flex key={brand.id} align="center" gap="2">
                        <Checkbox 
                          checked={editedAutomation.audience_brands.includes(brand.id)}
                          onCheckedChange={(checked) => handleBrandChange(brand.id, checked)}
                        />
                        <Text size="2">{brand.name}</Text>
                      </Flex>
                    ))}
                    {brandsData.filter(b => !b.is_default).length === 0 && (
                      <Text size="2" color="gray">No brands available</Text>
                    )}
                  </Flex>
                </Flex>
              )}

              {/* Location Selection (when audience_level is 'location') */}
              {editedAutomation?.audience_level === 'location' && locationsData && (
                <Flex direction="column" gap="1" style={{ alignSelf: 'stretch'}}>
                  <Text weight="medium" size="2">Select Locations</Text>
                  <Grid columns="3" gap="3" mt="2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {locationsData.map((location) => (
                      <Flex key={location.id} align="center" gap="2">
                        <Checkbox 
                          checked={editedAutomation.audience_locations.includes(location.id)}
                          onCheckedChange={(checked) => handleLocationChange(location.id, checked)}
                        />
                        <Text size="2" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {location.name}
                        </Text>
                      </Flex>
                    ))}
                  </Grid>
                  {locationsData.length === 0 && (
                    <Text size="2" color="gray" mt="2">No locations available</Text>
                  )}
                </Flex>
              )}

              {/* Eligibility filters (optional) */}
              {editedAutomation && (
                <Flex direction="column" gap="2" style={{ alignSelf: 'stretch' }}>
                  <Flex direction="column" gap="1">
                    <Text weight="medium" size="2">Eligibility filters (optional)</Text>
                    <Text size="1" color="gray">
                      Limit this automation to contacts matching attribute rules (e.g. age group, tags).
                      Leave empty to include every contact in the selected audience.
                    </Text>
                  </Flex>
                  <ContactFilterBuilder
                    matchMode={editedAutomation.eligibility_filters?.match || "all"}
                    onMatchModeChange={(val) => setEditedAutomation({
                      ...editedAutomation,
                      eligibility_filters: { ...editedAutomation.eligibility_filters, match: val },
                    })}
                    rules={editedAutomation.eligibility_filters?.rules || []}
                    onRulesChange={(rules) => setEditedAutomation({
                      ...editedAutomation,
                      eligibility_filters: { ...editedAutomation.eligibility_filters, rules },
                    })}
                    locations={locationsList}
                    tags={contactTags || []}
                  />
                </Flex>
              )}
            </Flex>
            <Separator orientation="vertical" size="4" style={dialogSeparatorStyle} />
            <TemplatePreviewPanel template={selectedEditTemplate} />
          </Flex>
          
          {/* Delete automation */}
          <Flex gap="3" mt="5" justify="between">
            <AlertDialog.Root open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <AlertDialog.Trigger>
                <Button color="red" variant="soft">Delete</Button>
              </AlertDialog.Trigger>
              <AlertDialog.Content style={{ maxWidth: 450 }}>
                <AlertDialog.Title>Delete Automation</AlertDialog.Title>
                <AlertDialog.Description size="2">
                  Are you sure? This automation will be permanently deleted.
                </AlertDialog.Description>

                <Flex gap="3" mt="4" justify="end">
                  <AlertDialog.Cancel>
                    <Button variant="soft" color="gray">Cancel</Button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action>
                    <Button variant="solid" color="red" onClick={handleDeleteAutomation} disabled={isDeleting}>
                      {isDeleting ? "Deleting..." : "Delete automation"}
                    </Button>
                  </AlertDialog.Action>
                </Flex>
              </AlertDialog.Content>
            </AlertDialog.Root>

            <Flex gap="3">
              <Dialog.Close>
                <Button variant="soft" color="gray">Close</Button>
              </Dialog.Close>
              <Button onClick={handleUpdateAutomation}
                disabled={
                  !hasChanges || isUpdating ||
                  (editedAutomation?.trigger_type === 'birthday' && (!editedAutomation?.birthday_send_time || !editedAutomation?.birthday_timezone))
                }
                loading={isUpdating}>
                Update automation
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      
      {/* Edit WhatsApp Automation Dialog */}
      <Dialog.Root open={!!selectedWhatsappAutomation} onOpenChange={(open) => !open && setSelectedWhatsappAutomation(null)}>
        <Dialog.Content style={{ height: '100%', width: 'min(1120px, calc(100vw - 32px))', maxWidth: 1120, display: 'flex', flexDirection: 'column' }}>
          <Dialog.Title>Edit Automation</Dialog.Title>
          <Dialog.Description size="2" mb="4" color="gray">
            Modify the automation settings below.
          </Dialog.Description>

          {editedWhatsappAutomation ? (
            <WhatsAppAutomationFields
              automation={editedWhatsappAutomation}
              setAutomation={setEditedWhatsappAutomation}
              templates={approvedWhatsappTemplates}
              brands={brandsData || []}
              locations={locationsData || []}
              titleSuffix="automation"
              organisationId={userProfile?.organisation_id}
              contactTags={contactTags || []}
            />
          ) : null}

          <Flex gap="3" mt="5" justify="between">
            <Button variant="soft" color="red" onClick={() => setIsWhatsappDeleteDialogOpen(true)} disabled={isDeleting}>
              Delete
            </Button>
            <Flex gap="3">
              <Dialog.Close>
                <Button variant="soft" color="gray">Close</Button>
              </Dialog.Close>
              <Button
                onClick={handleUpdateWhatsappAutomation}
                disabled={
                  isUpdating ||
                  !hasWhatsappChanges ||
                  !editedWhatsappAutomation?.name.trim() ||
                  !editedWhatsappAutomation?.associated_template_id ||
                  !editedWhatsappAutomation?.whatsapp_phone_number_id ||
                  (editedWhatsappAutomation?.trigger_type === "birthday" && (!editedWhatsappAutomation?.birthday_send_time || !editedWhatsappAutomation?.birthday_timezone))
                }
                loading={isUpdating}
              >
                Update automation
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      
      {/* Delete WhatsApp Automation Dialog */}
      <AlertDialog.Root open={isWhatsappDeleteDialogOpen} onOpenChange={setIsWhatsappDeleteDialogOpen}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Delete WhatsApp automation?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This removes the WhatsApp automation from marketing.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={handleDeleteWhatsappAutomation} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete automation"}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
    </>
  );
}
